// One of the employee's own Talk to HR requests: what they sent, its status, and any comments HR
// wrote for them in the portal. Most of HR's answers go by email, which Ask HR doesn't see; the
// page says so. "/requests/<id>" opens it in place of the conversation (keyed by id, so each
// request starts fresh).

import { useEffect, useState } from "react";
import { CATEGORIES, STATUS_LABELS, type EmployeeRequest, type EmployeeRequestDetail } from "@ask-hr/core/escalations";
import { ApiError, api } from "../api.js";
import { AttachPicker, AttachmentLinks, uploadAll } from "../attachments.js";
import { linkTo } from "../router.js";

export function requestIdOf(path: string): string | null {
  return /^\/requests\/([0-9a-z]{9,40})$/.exec(path)?.[1] ?? null;
}

// Which comments the employee has seen, kept in this browser only: enough to mark a request with
// a new comment, without the server tracking reads.
const SEEN_KEY = "novara-askhr-requests-seen";

function seenMap(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}") as unknown;
    return typeof v === "object" && v !== null ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function hasUnseenComment(r: EmployeeRequest): boolean {
  return Boolean(r.lastCommentAt && r.lastCommentAt > (seenMap()[r.id] ?? ""));
}

function markSeen(r: EmployeeRequest): void {
  if (!r.lastCommentAt) return;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ ...seenMap(), [r.id]: r.lastCommentAt }));
  } catch {
    // storage full or blocked: the marker just stays
  }
}

export const STATUS_PILL = { new: "pill alert", in_progress: "pill", resolved: "pill ok" } as const;
const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export function MyRequest({ id, onSeen }: { id: string; onSeen: () => void }) {
  const [request, setRequest] = useState<EmployeeRequestDetail | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function send() {
    setSending(true);
    setProblem(null);
    setSent(false);
    try {
      const base = `/api/escalations/${id}`;
      const attachments = files.length ? await uploadAll(base, files) : [];
      const r = await api<{ request: EmployeeRequestDetail }>("POST", `${base}/replies`, { note: reply, attachments });
      setRequest(r.request);
      setReply("");
      setFiles([]);
      setSent(true);
    } catch (e) {
      setProblem(e instanceof Error && e.message ? e.message : "Your reply didn't send. Try again.");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    let live = true;
    api<{ request: EmployeeRequestDetail }>("GET", `/api/escalations/${id}`).then(
      (r) => {
        if (!live) return;
        setRequest(r.request);
        markSeen(r.request);
        onSeen();
      },
      (e: unknown) => live && setError(e instanceof ApiError && e.status === 404 ? "not_found" : "failed"),
    );
    return () => {
      live = false;
    };
    // onSeen only refreshes the pane's markers; a new id is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="my-request panel">
      <p>
        <a href="/" onClick={linkTo("/")}>
          ← Back to Ask HR
        </a>
      </p>
      {error ? (
        <p className="notice" role="alert">
          {error === "not_found" ? "That request couldn't be found. It may be older than 90 days." : "Couldn't load the request. Try again."}
        </p>
      ) : !request ? (
        <p className="sub">Loading…</p>
      ) : (
        <>
          <div className="my-request-head">
            <h2>{CATEGORIES[request.category]}</h2>
            <span className={STATUS_PILL[request.status]}>{STATUS_LABELS[request.status]}</span>
          </div>
          <p className="sub">
            Sent to HR {when(request.createdAt)}
            {request.channel === "slack" ? " from Slack" : ""}.
          </p>

          {request.note ? (
            <>
              <h3>Your note</h3>
              <p className="prewrap">{request.note}</p>
            </>
          ) : null}
          {request.excerpt.length ? (
            <details className="shared">
              <summary>The conversation you shared ({request.excerpt.length} messages)</summary>
              <div className="extract">
                {request.excerpt.map((t, i) => (
                  <p key={i} className="prewrap">
                    <strong>{t.role === "user" ? "You" : "Ask HR"}:</strong> {t.text}
                  </p>
                ))}
              </div>
            </details>
          ) : null}

          <h3>Updates</h3>
          <ol className="timeline">
            {request.timeline.map((t, i) => (
              <li key={i} className={t.comment ? "comment" : undefined}>
                <span className="muted small">{when(t.at)}</span>
                {t.status ? <div>{t.status === "new" ? "Sent to HR" : `Marked ${STATUS_LABELS[t.status].toLowerCase()}`}</div> : null}
                {t.comment ? (
                  <div className={t.comment.mine ? "mine" : undefined}>
                    {t.comment.mine ? <strong>You</strong> : (
                      <>
                        <strong>{t.comment.by}</strong> <span className="muted">(HR)</span>
                      </>
                    )}
                    {t.comment.text ? <p className="prewrap">{t.comment.text}</p> : null}
                    {t.comment.attachments?.length ? (
                      <AttachmentLinks items={t.comment.attachments} path={(a) => `/api/escalations/${request.id}/attachments/${a.id}`} />
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
          <p className="sub">HR replies here. You get an email and a Slack message when they do.</p>

          <div className="field reply">
            <label htmlFor={`reply-${request.id}`}>Reply to HR</label>
            <textarea id={`reply-${request.id}`} rows={3} maxLength={2000} value={reply} disabled={sending} onChange={(e) => setReply(e.target.value)} />
            <AttachPicker files={files} onChange={setFiles} onProblem={setProblem} disabled={sending} />
            {problem ? (
              <p className="notice" role="alert">
                {problem}
              </p>
            ) : null}
            <div className="actions start">
              <button type="button" className="primary" disabled={sending || (!reply.trim() && !files.length)} onClick={() => void send()}>
                {sending ? "Sending…" : "Send reply"}
              </button>
              {sent ? (
                <span className="muted small" role="status">
                  Sent. HR will see it on your request.
                </span>
              ) : null}
            </div>
            <span className="muted small">
              Only you and HR see this.{request.status === "resolved" ? " Replying opens the request again." : ""}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
